import { useEffect, useRef } from "react";

import type { ExtractResource } from "@prequel/import-react";
import { useListExtracts } from "@prequel/import-react";
import { IdCell } from "@prequel/shared-components";

import ImportJobStatusBadge from "@/components/ImportJobStatusBadge";
import ResourceTable from "@/components/ResourceTable";
import { useDatasetContext } from "@/pages/import/DatasetsPage/DatasetPage";
import { getJobStatus } from "@/pages/import/jobStatus";
import { getDuration, getRelativeSubmittedAt } from "@/types";

const COLUMNS = [
  "Extract ID",
  "Status",
  "Submitted",
  "Duration",
  "Processed",
  "Inserted",
  "Updated",
  "Deleted",
  "Errored",
];

const formatCount = (value: number | null | undefined) => value?.toLocaleString() ?? "-";

const useExtractsWithRefresh = (datasetId: string, refreshKey: number, params: { pageSize?: number }) => {
  const query = useListExtracts(datasetId, params);
  const { refetch } = query;
  const previousRefreshKey = useRef(refreshKey);

  useEffect(() => {
    if (previousRefreshKey.current !== refreshKey) {
      previousRefreshKey.current = refreshKey;
      refetch();
    }
  }, [refreshKey, refetch]);

  return query;
};

const ExtractsTable = () => {
  const { dataset, extractsRefreshKey = 0 } = useDatasetContext();
  const datasetId = dataset.id;

  const useExtracts = (params: { pageSize?: number }) => useExtractsWithRefresh(datasetId, extractsRefreshKey, params);

  const rowMapper = (extract: ExtractResource) => ({
    id: extract.id,
    to: `/import/datasets/${datasetId}/extracts/${extract.id}`,
    cells: [
      { value: <IdCell id={extract.id} /> },
      { value: <ImportJobStatusBadge status={getJobStatus(extract)} /> },
      { value: getRelativeSubmittedAt(extract.submitted_at) },
      { value: getDuration({ started_at: extract.started_at, ended_at: extract.ended_at }) },
      { value: formatCount(extract.records_processed) },
      { value: formatCount(extract.records_new_inserted) },
      { value: formatCount(extract.records_existing_updated) },
      { value: formatCount(extract.records_existing_deleted) },
      { value: formatCount(extract.records_errored) },
    ],
  });

  return (
    <ResourceTable<ExtractResource>
      columns={COLUMNS}
      useQuery={useExtracts}
      rowMapper={rowMapper}
      emptyMessage="No extracts found for this dataset."
    />
  );
};

export default ExtractsTable;
